import { executeManifestQuery } from "./modules.service";
import { formatCurrency, formatPercentage } from "@/utils/format";

const MODULE_ID = "consulta-de-produto";

export interface Product {
  title: string;
  code: string;
}

export interface ListProductsParams {
  filtro: string;
  ean: string;
}

export const getListProducts = (params: ListProductsParams, clientId: string) =>
  executeManifestQuery<Product[]>(MODULE_ID, "consulta", params, clientId);

interface ProductDetailRaw {
  categoria: string;
  comprador: string;
  custoliquido: string;
  dataultimaentrada: string | null;
  dataultimavenda: string | null;
  imagemurl: string;
  margem: string;
  mdv: string;
  nroempresa: string;
  preconormal: string;
  situacao: string;
  estoquedisponivel: string;
  estoquereservado: string;
  estoquetransito: string;
  estoquefuturo: string;
}

export interface ProductDetailSection {
  label: string;
  value: string;
  entrypoint?: string;
}

export interface ProductCompanyDetail {
  imageUrl: string;
  companyErp: string;
  category: string;
  lastEntry?: string;
  lastSale?: string;
  sections: ProductDetailSection[];
}

const mapToProductDetail = (raw: ProductDetailRaw[]): ProductCompanyDetail[] => {
  const seen = new Set<string>();
  const deduped = raw.filter((item) => {
    if (seen.has(item.nroempresa)) return false;
    seen.add(item.nroempresa);
    return true;
  });

  return deduped.map((item) => ({
    imageUrl: item.imagemurl,
    companyErp: item.nroempresa,
    category: item.categoria,
    lastEntry: item.dataultimaentrada ?? undefined,
    lastSale: item.dataultimavenda ?? undefined,
    sections: [
      { label: "Preço", value: formatCurrency(Number(item.preconormal)), entrypoint: "detalhe_preco" },
      { label: "Margem", value: formatPercentage(item.margem) },
      { label: "MDV", value: String(item.mdv).replace(".", ","), entrypoint: "detalhe_mdv" },
      { label: "Situação do Preço", value: item.situacao, entrypoint: "detalhe_situacao_vigente" },
      { label: "Custo Líquido", value: formatCurrency(Number(item.custoliquido)), entrypoint: "detalhe_custo_liquido" },
      { label: "Estoque Disponível", value: item.estoquedisponivel, entrypoint: "detalhe_estoque_disponivel" },
      { label: "Estoque Reservado", value: item.estoquereservado },
      { label: "Estoque Trânsito", value: item.estoquetransito, entrypoint: "detalhe_estoque_transito" },
      { label: "Estoque Futuro", value: item.estoquefuturo },
    ],
  }));
};

export const getProductDetail = async (codproduto: string, clientId: string) => {
  const raw = await executeManifestQuery<ProductDetailRaw[]>(MODULE_ID, "detalhe", { codproduto }, clientId);
  return mapToProductDetail(raw);
};

// ── Tabelas de detalhe (drill-down por indicador) ──────────────────────────

export interface DetailTableParams {
  codproduto: string;
  nrempresa: string;
}

export interface ProductPriceDetail {
  embalagem: string;
  preco_embalagem: number;
  preco_unidade: number;
  margem: number;
}

export interface ProductDailySale {
  data: string;
  diasemana: string;
  qtd: number;
  qtd_inicial: number;
}

export interface ProductPromotionHistoric {
  dtainicio: string;
  dtafim: string;
  embalagem: number;
  preco: number;
}

export interface ProductDailyCust {
  dtaentradasaida: string;
  estqinicial: number;
  estqfinal: number;
  custo: number;
}

export interface ProductDailyStock {
  dtaentradasaida: string;
  qtdentrada: number;
  qtdsaida: number;
  qtdsaldo: number;
}

export interface ProductStockTransit {
  numeronf: string;
  serienf: number;
  dtaemissao: string;
  quantidade: number;
}

export const getPriceDetail = (params: DetailTableParams, clientId: string) =>
  executeManifestQuery<ProductPriceDetail[]>(MODULE_ID, "detalhe_preco", params, clientId);

export const getDailySale = (params: DetailTableParams, clientId: string) =>
  executeManifestQuery<ProductDailySale[]>(MODULE_ID, "detalhe_mdv", params, clientId);

export const getPromotionHistoric = (params: DetailTableParams, clientId: string) =>
  executeManifestQuery<ProductPromotionHistoric[]>(MODULE_ID, "detalhe_situacao_vigente", params, clientId);

export const getDailyCust = (params: DetailTableParams, clientId: string) =>
  executeManifestQuery<ProductDailyCust[]>(MODULE_ID, "detalhe_custo_liquido", params, clientId);

export const getDailyStock = (params: DetailTableParams, clientId: string) =>
  executeManifestQuery<ProductDailyStock[]>(MODULE_ID, "detalhe_estoque_disponivel", params, clientId);

export const getStockTransit = (params: DetailTableParams, clientId: string) =>
  executeManifestQuery<ProductStockTransit[]>(MODULE_ID, "detalhe_estoque_transito", params, clientId);
